import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, shareReplay } from 'rxjs/operators';
import { Observable, combineLatest } from 'rxjs';
import { AuthSelectors } from '@/app/+state/auth';
import { CaslAbilityFactory, AppAbility, CaslAction, RbacSubjects } from './casl-ability.factory';
import { User } from '@/app/shared/interfaces';

@Injectable({
    providedIn: 'root',
})
export class PermissionsService {
    private readonly store = inject(Store);
    private readonly abilityFactory = inject(CaslAbilityFactory);

    // Observable of current user's abilities
    public readonly ability$: Observable<AppAbility> = this.store.select(AuthSelectors.selectUser).pipe(
        map((user: User | null) => this.abilityFactory.createForUser(user)),
        shareReplay(1)
    );

    // Observable of current user
    public readonly currentUser$ = this.store.select(AuthSelectors.selectUser);

    /**
     * Check if current user can perform an action on a subject
     */
    can$(action: CaslAction, subject: RbacSubjects): Observable<boolean> {
        return this.ability$.pipe(
            map(ability => ability.can(action, subject))
        );
    }

    /**
     * Check if current user cannot perform an action on a subject
     */
    cannot$(action: CaslAction, subject: RbacSubjects): Observable<boolean> {
        return this.ability$.pipe(
            map(ability => ability.cannot(action, subject))
        );
    }

    /**
     * Synchronous check if current user can perform an action
     * Note: This requires the ability to be already loaded
     */
    can(action: CaslAction, subject: RbacSubjects): boolean {
        const user = this.store.selectSignal(AuthSelectors.selectUser)();
        const ability = this.abilityFactory.createForUser(user);
        return ability.can(action, subject);
    }

    /**
     * Synchronous check if current user cannot perform an action
     * Note: This requires the ability to be already loaded
     */
    cannot(action: CaslAction, subject: RbacSubjects): boolean {
        const user = this.store.selectSignal(AuthSelectors.selectUser)();
        const ability = this.abilityFactory.createForUser(user);
        return ability.cannot(action, subject);
    }

    /**
     * Get all permissions for current user as an observable
     */
    getUserPermissions$(): Observable<Record<string, string[]> | null> {
        return this.currentUser$.pipe(
            map(user => {
                if (!user?.role) return null;
                return this.abilityFactory.getRolePermissionSummary(user.role as any);
            })
        );
    }

    /**
     * Helper methods for common permission checks
     */
    canCreateProducts$(): Observable<boolean> {
        return this.can$(CaslAction.Create, RbacSubjects.PRODUCTS);
    }

    canEditProducts$(): Observable<boolean> {
        return this.can$(CaslAction.Update, RbacSubjects.PRODUCTS);
    }

    canDeleteProducts$(): Observable<boolean> {
        return this.can$(CaslAction.Delete, RbacSubjects.PRODUCTS);
    }

    canCreateOrganizations$(): Observable<boolean> {
        return this.can$(CaslAction.Create, RbacSubjects.ORGANIZATIONS);
    }

    canEditOrganizations$(): Observable<boolean> {
        return this.can$(CaslAction.Update, RbacSubjects.ORGANIZATIONS);
    }

    canDeleteOrganizations$(): Observable<boolean> {
        return this.can$(CaslAction.Delete, RbacSubjects.ORGANIZATIONS);
    }

    canManageUsers$(): Observable<boolean> {
        return combineLatest([
            this.can$(CaslAction.Create, RbacSubjects.USERS),
            this.can$(CaslAction.Update, RbacSubjects.USERS),
            this.can$(CaslAction.Delete, RbacSubjects.USERS)
        ]).pipe(
            map(([canCreate, canUpdate, canDelete]) => canCreate || canUpdate || canDelete)
        );
    }

    canViewUsers$(): Observable<boolean> {
        return this.can$(CaslAction.Read, RbacSubjects.USERS);
    }

    // Synchronous versions for template usage
    canCreateProducts(): boolean {
        return this.can(CaslAction.Create, RbacSubjects.PRODUCTS);
    }

    canEditProducts(): boolean {
        return this.can(CaslAction.Update, RbacSubjects.PRODUCTS);
    }

    canDeleteProducts(): boolean {
        return this.can(CaslAction.Delete, RbacSubjects.PRODUCTS);
    }

    canCreateOrganizations(): boolean {
        return this.can(CaslAction.Create, RbacSubjects.ORGANIZATIONS);
    }

    canEditOrganizations(): boolean {
        return this.can(CaslAction.Update, RbacSubjects.ORGANIZATIONS);
    }

    canDeleteOrganizations(): boolean {
        return this.can(CaslAction.Delete, RbacSubjects.ORGANIZATIONS);
    }

    canManageUsers(): boolean {
        return this.can(CaslAction.Create, RbacSubjects.USERS) || 
               this.can(CaslAction.Update, RbacSubjects.USERS) || 
               this.can(CaslAction.Delete, RbacSubjects.USERS);
    }

    canViewUsers(): boolean {
        return this.can(CaslAction.Read, RbacSubjects.USERS);
    }
}
